import { matchPath } from 'react-router'

import axios from 'axios'
import { pickBy } from 'lodash-es'
import { Store } from 'redux'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import {
  API_REQUEST_HEADERS,
  AUTH_LANDING_VALUE,
  clearStorageDataOnLogout,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  getDevFlags,
  GOODNESS_ESSENTIALS_URL,
  GOOGLE_PERFORMANCE_URL,
  PAGE_ROUTES,
  setContextUUIDHeader,
} from '@lyrahealth-inc/shared-app-logic'
import { UTM_CAMPAIGN } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'

import { setIsLogoutInProgress } from '../../data/logout/logoutActions'
import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { getId, getUserDisplayLanguageOrDefault } from '../../data/user/userSelectors'
import { CSRF_HEADER_NAME, CSRF_TOKEN } from '../constants/appConstants'
import { clearPendoSession } from '../utils/pendoLoader'
import { updateStoredProviderResultsSurveyValues } from '../utils/pendoUtils'
import { logToSumoLogic, SUMO_CATEGORY } from '../utils/userUtils'

const NO_X_FEATURES_URLS = [/(?:https?:\/\/)vendorapi\.([a-zA-Z0-9-]+\.)?lyrahealth\.com\/.*/]

// baseURL Works in almost all cases, may need to be overridden a few times
const instance = axios.create({
  baseURL: '/services',
})

export const essentialsAxiosInstance = axios.create({
  baseURL: GOODNESS_ESSENTIALS_URL,
})

const interceptor = (store: Store): void => {
  instance.interceptors.response.use(
    function (response) {
      return response
    },
    function (error) {
      if (error.response && error.response.status === 401) {
        const hostname = window.location.hostname
        let mixpanelEntryPoint = ''
        if (!matchPath('register/mfa/:id/verify', window.location.pathname)) {
          const redirectUrl = `${window.location.pathname + window.location.search + window.location.hash}`
          window.sessionStorage.setItem(UNAUTHENTICATED_REDIRECT_URL, redirectUrl)
          if (redirectUrl.includes(GOOGLE_PERFORMANCE_URL)) {
            mixpanelEntryPoint = UTM_CAMPAIGN.GOOGLE_CRITICAL_TALENT
          } else if (redirectUrl.includes(PAGE_ROUTES.PARENTAL_LEAVE_COACHING)) {
            mixpanelEntryPoint = COMMON_MIXPANEL_PAGE.PARENTAL_LEAVE_COACHING
          }
        }
        store.dispatch(setIsLogoutInProgress(true))
        mixpanel.reset()
        clearPendoSession()
        updateStoredProviderResultsSurveyValues()
        clearStorageDataOnLogout()
        const searchParams = new URLSearchParams(window.location.search)
        let authLanding = searchParams.get('authLanding')
        let targetUrl = ''
        const additionalQueryParams = []
        const defaultAuthLanding = {
          [PAGE_ROUTES.GOOGLE_PERFORMANCE]: AUTH_LANDING_VALUE.REGISTRATION,
          [PAGE_ROUTES.PARENTAL_LEAVE_COACHING]: AUTH_LANDING_VALUE.REGISTRATION,
        }

        const getUrl = (targetUrl: string, additionalQueryParams?: string[]) => {
          // persist query params (including utm params)
          const hasExistingParams = window.location.search.length > 0
          const separator = hasExistingParams ? '&' : '?'
          return `${targetUrl}${window.location.search}${
            additionalQueryParams?.length ? `${separator}${additionalQueryParams.join('&')}` : ''
          }`
        }

        if (window.location.pathname.includes(PAGE_ROUTES.ESSENTIALS)) {
          if (window.location.pathname.includes('articles')) {
            const contentSlug = window.location.pathname.split('/').pop()
            targetUrl = `${PAGE_ROUTES.ESSENTIALS_ARTICLES}/${contentSlug}`
          } else {
            targetUrl = PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET
          }
          window.location.replace(getUrl(targetUrl))
        } else {
          if (!authLanding) {
            authLanding = defaultAuthLanding[window.location.pathname]
          }
          if (authLanding === AUTH_LANDING_VALUE.REGISTRATION) {
            targetUrl = '/register/connect'
          } else if (hostname.startsWith('care')) {
            // If on care subdomain, redirect to landing page
            targetUrl = '/'
            additionalQueryParams.push('findEmployerSubmitRedirect=register/connect')
          } else {
            targetUrl = '/login'
          }
          if (mixpanelEntryPoint) {
            additionalQueryParams.push(`${COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT}=${mixpanelEntryPoint}`)
          }
          window.location.href = getUrl(targetUrl, additionalQueryParams)
        }
      }
      const sourceCategory =
        process.env.NODE_ENV === 'production' ? SUMO_CATEGORY.PROD_DEBUG : SUMO_CATEGORY.STAGING_DEBUG
      const state = store.getState()
      const lyraId = getId(state)
      logToSumoLogic(sourceCategory, lyraId, {
        messsage: `Request Error - ${error}`,
        type: 'error',
        error,
        sourceCategory: process.env.NODE_ENV === 'production' ? SUMO_CATEGORY.PROD_DEBUG : SUMO_CATEGORY.STAGING_DEBUG,
      })
      return Promise.reject(error)
    },
  )

  instance.interceptors.request.use(function (config) {
    const csrfHeaderName = localStorage.getItem(CSRF_HEADER_NAME)
    const csrfToken = localStorage.getItem(CSRF_TOKEN)
    if (csrfHeaderName && csrfToken && config.headers) {
      config.headers[csrfHeaderName] = csrfToken
    }

    // Pass dev flags from test hooks to LW API via response header
    const { url } = config
    const skipFeaturesHeader = !url || NO_X_FEATURES_URLS.some((regex) => regex.test(url))
    const enabledFeatureFlags = Object.keys(pickBy(getDevFlags(), (value) => value === true))
    if (!skipFeaturesHeader && enabledFeatureFlags.length > 0 && config.headers) {
      config.headers[API_REQUEST_HEADERS.X_FEATURES] = enabledFeatureFlags.join(',')
    }

    setContextUUIDHeader(config)

    const state = store.getState()
    const userDisplayLanguage = getUserDisplayLanguageOrDefault(state)
    if (userDisplayLanguage && config.headers) {
      config.headers[API_REQUEST_HEADERS.X_LYRA_LOCALE] = userDisplayLanguage
    }

    return config
  })
}

export default {
  interceptor,
}

export const axiosInstance = instance
